import { createRouter, createWebHistory } from 'vue-router'
import {
	AuthGuard,
	noEnforcementGuard,
	requireLoggedInGuard,
	requireLoggedOutGuard,
	requireAdminGuard,
	requireDataUploadGuard,
	requireDocumentUploadGuard,
	requireDocumentDownloadGuard,
	requirePermissionsGuard
} from '@/router/guards/AuthGuard.js'
import { Tenants } from '@/models/tenants/Tenants.js'
import AdminRoutes from '@/router/routes/AdminRoutes'
import AuthRoutes from '@/router/routes/AuthRoutes'
import DashboardRoutes from '@/router/routes/DashboardRoutes'
import ErrorRoutes from '@/router/routes/ErrorRoutes'
import DataRoutes from '@/router/routes/DataRoutes.js'
import DocumentRoutes from '@/router/routes/DocumentRoutes.js'
import SettingsRoutes from '@/router/routes/SettingsRoutes'
import LoginPage from '@/views/Auth/LoginPage.vue'
import { UserpilotService } from '@/models/utils/UserpilotUtils.js'
import { useAuthStore } from '@/stores/authStore.js'

const routes = [
	{
		path: '/',
		name: 'default',
		component: LoginPage,
		meta: {
			authGuard: AuthGuard.RequireLoggedOut,
			title: 'Login'
		}
	},
	...AdminRoutes,
	...AuthRoutes,
	...DataRoutes,
	...DocumentRoutes,
	...SettingsRoutes,
	...DashboardRoutes,
	...ErrorRoutes,
]

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes,
	scrollBehavior(to, from, savedPosition)
	{
		return savedPosition || { left: 0, top: 0 }
	}
})

router.beforeEach((to, from, next) =>
{
	document.title = `${to.meta.title} | ${Tenants.getTenantAppName()}`
	if (!to.meta || !Object.prototype.hasOwnProperty.call(to.meta, 'authGuard'))
	{
		throw '"authGuard" not set for route.'
	}
	switch (to.meta.authGuard)
	{
		case AuthGuard.NoEnforcement:
			noEnforcementGuard(to, from, next)
			break
		case AuthGuard.RequireLoggedIn:
			requireLoggedInGuard(to, from, next)
			break
		case AuthGuard.RequireLoggedOut:
			requireLoggedOutGuard(to, from, next)
			break
		case AuthGuard.RequireAdmin:
			requireAdminGuard(to, from, next)
			break
		case AuthGuard.RequireDataUpload:
			requireDataUploadGuard(to, from, next)
			break
		case AuthGuard.RequireDocumentUpload:
			requireDocumentUploadGuard(to, from, next)
			break
		case AuthGuard.RequireDocumentDownload:
			requireDocumentDownloadGuard(to, from, next)
			break
		case AuthGuard.RequirePermissions:
			requirePermissionsGuard(to, from, next)
			break
		default:
			throw 'Unknown "authGuard" property defined on route: "' + to.meta.authGuard + '"'
	}
})

/**
 * Required for Userpilot. Tells Userpilot to re-evaluate its content for the new page.
 *
 * Three things here are load-bearing and not obvious:
 *
 *  - afterEach, not beforeEach: vue-router commits the URL in finalizeNavigation() before
 *    triggering the afterEach hooks, so this is the first hook where the current URL is the
 *    one the user actually landed on. The auth guards redirect constantly, and beforeEach
 *    would report every target they redirect away from.
 *  - the p_failure check: afterEach still fires for aborted, cancelled and duplicated
 *    navigations, where the URL never changed. The sidebar logo links to 'dashboard' from
 *    every page including /dashboard, so clicking it there is a duplicate navigation and
 *    would otherwise report a page view that never happened.
 *  - identifying here: the login views are the only other callers of userpilotIdentify(),
 *    so after a browser refresh of a live session nothing has identified the user and
 *    Userpilot would ignore every reload() for the rest of the page load.
 *
 * @param {Object} p_to Route navigated to
 * @param {Object} p_from Route navigated away from
 * @param {Object} [p_failure] Set when the navigation was aborted, cancelled or duplicated
 */
router.afterEach((p_to, p_from, p_failure) =>
{
	if (p_failure) return;

	if (!UserpilotService.isIdentified())
	{
		const authStore = useAuthStore();

		//Never report a pre-auth URL: reset-password and register carry a live token and
		//the user's email in the query string.
		if (!authStore.isLoggedIn) return;

		authStore.userpilotIdentify();
	}

	UserpilotService.reload();
})

export default router
